/**
 * Eingabe: mehrzeilig (Enter sendet, Shift+Enter neue Zeile), Zeichenzähler, Autovervollständigung @ # :,
 * Antworten, Pfeil-hoch bearbeitet die letzte eigene Nachricht, Anhänge (Button/Einfügen; Drag & Drop über
 * MessagePane), Emoji-/Sticker-Auswahl, Tipp-Anzeige (gedrosselt), Slowmode und fehlende Rechte.
 */
import { isDm, isThread, type ChannelDto, type MemberDto, type StickerDto, type UserDto } from '@lagerfeuer/shared';
import { EyeOff, FileText, Plus, SendHorizontal, Smile, Sticker, Timer, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Avatar } from '../../components/Avatar';
import { EmojiGlyph, EmojiPickerPopover, loadUnicodeEmojis, pushRecentEmoji, searchUnicodeEmojis, type PickedEmoji } from '../../components/EmojiPicker';
import { anchorFrom, type Anchor } from '../../components/Popover';
import { StickerPickerPopover } from '../../components/StickerPicker';
import { Tooltip } from '../../components/Tooltip';
import { channelsApi, membersApi } from '../../lib/endpoints';
import { gatewaySend } from '../../realtime/gateway';
import { useAppStore } from '../../store/app';
import type { AppData } from '../../store/types';
import { can, channelPermissions, dmName, groupServerChannels, memberColor } from '../../store/selectors';
import { suggest, type Suggestion, type SuggestSource } from './autocomplete';
import { applyCompletion, findTrigger, toRaw, type TokenMap, type Trigger } from './composerText';
import { allServerEmojis } from './emojiCache';
import { queueFiles, removeFile, toggleSpoiler } from './files';
import { formatBytes } from './format';
import { useAuthor } from './MessageItem';
import { isSystemMessage } from './grouping';
import type { MessagePerms } from './perms';
import { sendMessage } from './send';
import { drafts, registerComposerInput, typingSentAt, useMessagesUi } from './state';
import { useCountdown } from './hooks';

/** Tipp-Ereignis höchstens alle 8 s (Gültigkeit serverseitig 10 s). */
export const TYPING_INTERVAL_MS = 8000;
const tokenMaps = new Map<string, TokenMap>();
const EMPTY_FILES: never[] = [];

/** Zuordnung Anzeige-Token → Rohsyntax des Entwurfs eines Kanals. */
function tokenMap(channelId: string): TokenMap {
  let m = tokenMaps.get(channelId);
  if (!m) tokenMaps.set(channelId, (m = {}));
  return m;
}

/** Tipp-Ereignis senden, gedrosselt auf TYPING_INTERVAL_MS je Kanal. */
export function maybeSendTyping(channelId: string): void {
  const now = Date.now();
  if (now - (typingSentAt.get(channelId) ?? 0) < TYPING_INTERVAL_MS) return;
  typingSentAt.set(channelId, now);
  void gatewaySend('TYPING_START', { channelId }).then((res) => {
    if (res === null) channelsApi.typing(channelId).catch(() => undefined);
  });
}

/** Quelle für Vorschläge aus dem aktuellen Store-Zustand. */
function suggestSource(s: AppData, channel: ChannelDto, perms: MessagePerms, unicodeReady: boolean): SuggestSource {
  const serverId = channel.serverId;
  let people: { user: UserDto; name: string }[] = [];
  if (serverId) {
    people = Object.values(s.members[serverId] ?? {}).map((m: MemberDto) => ({ user: m.user, name: m.nickname || m.user.displayName || m.user.username }));
  } else {
    const parent = channel.parentId ? s.channels[channel.parentId] : undefined;
    const recips = channel.recipients ?? parent?.recipients ?? [];
    const all = s.me ? [...recips.filter((u) => u.id !== s.me!.id), s.me] : recips;
    people = all.map((u) => ({ user: u, name: u.displayName || u.username }));
  }
  const roles = serverId ? (s.roles[serverId] ?? []) : [];
  const channels = serverId ? groupServerChannels(s, serverId).flatMap((g) => (g.category ? [g.category, ...g.channels] : g.channels)) : [];
  const custom = allServerEmojis(s).filter((e) => perms.allowExternalEmojis || e.serverId === serverId);
  return {
    people,
    roles,
    canMentionEveryone: perms.canMentionEveryone && !!serverId,
    channels: channels.filter((c) => can(channelPermissions(s, c.id), 'VIEW_CHANNEL')),
    customEmojis: custom,
    unicode: (q, limit) => (unicodeReady ? searchUnicodeEmojis(q, limit) : []),
    roleColor: (r) => memberColor([r], [r.id]),
  };
}

function SuggestionRow({ s }: { s: Suggestion }) {
  switch (s.kind) {
    case 'user':
      return (
        <>
          <Avatar user={s.user} size={24} />
          <span className="ac-label truncate">{s.label}</span>
          <span className="ac-sub faint small truncate">{s.sub}</span>
        </>
      );
    case 'role':
      return (
        <>
          <span className="ac-icon" style={s.color ? { color: s.color } : undefined} aria-hidden="true">
            @
          </span>
          <span className="ac-label truncate" style={s.color ? { color: s.color } : undefined}>
            {s.label}
          </span>
          <span className="ac-sub faint small">Rolle</span>
        </>
      );
    case 'everyone':
      return (
        <>
          <span className="ac-icon" aria-hidden="true">
            @
          </span>
          <span className="ac-label">{s.label}</span>
          <span className="ac-sub faint small truncate">{s.sub}</span>
        </>
      );
    case 'channel':
      return (
        <>
          <span className="ac-icon" aria-hidden="true">
            #
          </span>
          <span className="ac-label truncate">{s.label}</span>
        </>
      );
    case 'emoji':
      return (
        <>
          <EmojiGlyph emoji={s.emoji} size={22} />
          <span className="ac-label truncate">{s.label}</span>
        </>
      );
  }
}

function ReplyBar({ channelId, messageId, serverId }: { channelId: string; messageId: string; serverId: string | null }) {
  const msg = useAppStore((s) => s.messages[channelId]?.items.find((m) => m.id === messageId));
  const author = useAuthor(msg ?? { author: null, member: null, webhookId: null, overrideName: null, overrideAvatarUrl: null }, serverId);
  return (
    <div className="composer-bar reply-bar" data-testid="reply-bar">
      <span className="truncate">
        Antwort an <strong style={author.color ? { color: author.color } : undefined}>{msg ? author.name : 'Nachricht'}</strong>
      </span>
      <button type="button" className="icon-btn" onClick={() => useMessagesUi.getState().setReply(channelId, null)} aria-label="Antwort abbrechen" data-testid="reply-cancel">
        <X size={16} />
      </button>
    </div>
  );
}

function FileTray({ channelId }: { channelId: string }) {
  const files = useMessagesUi((s) => s.files[channelId]) ?? EMPTY_FILES;
  const previews = useMemo(
    () => new Map(files.filter((f) => f.file.type.startsWith('image/') && typeof URL.createObjectURL === 'function').map((f) => [f.id, URL.createObjectURL(f.file)])),
    [files],
  );
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);
  if (!files.length) return null;
  return (
    <ul className="file-tray" data-testid="attachment-tray" aria-label="Ausgewählte Dateien">
      {files.map((f) => (
        <li key={f.id} className={`file-tray-item ${f.spoiler ? 'spoiler' : ''}`} data-testid="attachment-item">
          <div className="file-tray-preview">{previews.get(f.id) ? <img src={previews.get(f.id)} alt="" /> : <FileText size={32} aria-hidden="true" />}</div>
          <span className="file-tray-name truncate" title={f.file.name}>
            {f.file.name}
          </span>
          <span className="faint small">{formatBytes(f.file.size)}</span>
          <div className="file-tray-actions">
            <Tooltip label={f.spoiler ? 'Spoiler entfernen' : 'Als Spoiler markieren'}>
              <button type="button" className="icon-btn" aria-pressed={f.spoiler} onClick={() => toggleSpoiler(channelId, f.id)} data-testid="attachment-spoiler">
                <EyeOff size={16} />
              </button>
            </Tooltip>
            <Tooltip label="Datei entfernen">
              <button type="button" className="icon-btn danger" onClick={() => removeFile(channelId, f.id)} data-testid="attachment-remove">
                <X size={16} />
              </button>
            </Tooltip>
          </div>
        </li>
      ))}
    </ul>
  );
}

function disabledReason(channel: ChannelDto, perms: MessagePerms, timeoutUntil: string | null | undefined): string {
  if (perms.bridgePaused) return 'Die Discord-Spiegelung ist pausiert – der Kanal ist schreibgeschützt.';
  if (perms.readOnly) return 'Dieser Kanal wird gespiegelt und ist schreibgeschützt.';
  if (timeoutUntil && new Date(timeoutUntil).getTime() > Date.now()) {
    return `Du bist bis ${new Date(timeoutUntil).toLocaleString('de-DE')} im Timeout und kannst nichts schreiben.`;
  }
  if (isThread(channel.type) && channel.thread?.locked) return 'Dieser Thread ist gesperrt.';
  if (isThread(channel.type) && channel.thread?.archived) return 'Dieser Thread ist archiviert.';
  return 'Du hast keine Berechtigung, in diesem Kanal Nachrichten zu senden.';
}

export function Composer({ channel, perms, panel }: { channel: ChannelDto; perms: MessagePerms; panel?: boolean }) {
  const channelId = channel.id;
  const serverId = channel.serverId;
  const meId = useAppStore((s) => s.me?.id);
  const maxLen = useAppStore((s) => s.limits.maxMessageLength);
  const timeoutUntil = useAppStore((s) => (serverId ? s.servers[serverId]?.me.timeoutUntil : null));
  const placeholderName = useAppStore((s) => (isDm(channel.type) ? `@${dmName(channel, s.me?.id)}` : `#${channel.name ?? ''}`));
  const replyTo = useMessagesUi((s) => s.replyTo[channelId] ?? null);
  const files = useMessagesUi((s) => s.files[channelId]) ?? EMPTY_FILES;
  const cooldownUntil = useMessagesUi((s) => s.cooldownUntil[channelId] ?? 0);

  const [text, setText] = useState(() => drafts.get(channelId) ?? '');
  const [trigger, setTrigger] = useState<Trigger | null>(null);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [unicodeReady, setUnicodeReady] = useState(false);
  const [memberVersion, setMemberVersion] = useState(0);
  const [picker, setPicker] = useState<{ kind: 'emoji' | 'sticker'; anchor: Anchor } | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number | null>(null);

  const cooldownLeft = useCountdown(cooldownUntil);
  const slowmode = channel.slowmodeSeconds > 0 && !perms.bypassSlowmode;

  // Eingabefeld registrieren (Fokus von außen, z. B. „Antworten“).
  const setInput = useCallback(
    (el: HTMLTextAreaElement | null) => {
      inputRef.current = el;
      registerComposerInput(channelId, el);
    },
    [channelId],
  );

  // Automatische Höhe.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, panel ? 200 : 320)}px`;
    if (caretRef.current !== null) {
      el.setSelectionRange(caretRef.current, caretRef.current);
      caretRef.current = null;
    }
  }, [text, panel]);

  // Unicode-Daten für „:“ nachladen.
  useEffect(() => {
    if (trigger?.kind === ':' && !unicodeReady) {
      loadUnicodeEmojis()
        .then(() => setUnicodeReady(true))
        .catch(() => undefined);
    }
  }, [trigger, unicodeReady]);

  // Mitglieder serverseitig suchen (große Server sind nur teilweise geladen).
  useEffect(() => {
    if (trigger?.kind !== '@' || !serverId || trigger.query.length < 1) return;
    const q = trigger.query;
    const t = setTimeout(() => {
      membersApi
        .list(serverId, { q, limit: 10 })
        .then((page) => {
          if (!page?.items?.length) return;
          useAppStore.getState().update((d) => {
            d.members[serverId] ??= {};
            for (const m of page.items) {
              d.members[serverId]![m.user.id] ??= m;
              d.users[m.user.id] ??= m.user;
            }
          });
          setMemberVersion((v) => v + 1);
        })
        .catch(() => undefined);
    }, 250);
    return () => clearTimeout(t);
  }, [trigger, serverId]);

  const suggestions = useMemo<Suggestion[]>(() => {
    if (!trigger) return [];
    if (dismissed === `${trigger.start}:${trigger.kind}`) return [];
    void memberVersion;
    return suggest(trigger, suggestSource(useAppStore.getState(), channel, perms, unicodeReady));
  }, [trigger, dismissed, channel, perms, unicodeReady, memberVersion]);
  const acOpen = suggestions.length > 0;

  const updateTrigger = (value: string, caret: number | null) => {
    const t = caret === null ? null : findTrigger(value, caret);
    setTrigger(t);
    setActive(0);
  };

  const setValue = (value: string, caret?: number) => {
    setText(value);
    if (value) drafts.set(channelId, value);
    else drafts.delete(channelId);
    if (caret !== undefined) caretRef.current = caret;
  };

  const complete = (s: Suggestion) => {
    if (!trigger) return;
    const { text: next, caret } = applyCompletion(text, trigger, s.insert);
    if (s.raw !== s.insert) tokenMap(channelId)[s.insert] = s.raw;
    if (s.kind === 'emoji') pushRecentEmoji(s.emoji);
    setValue(next, caret);
    setTrigger(null);
    inputRef.current?.focus();
  };

  const insertAtCaret = (insert: string) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const pre = text.slice(0, start);
    const sep = pre && !/\s$/.test(pre) ? ' ' : '';
    const next = `${pre}${sep}${insert} ${text.slice(end)}`;
    setValue(next, pre.length + sep.length + insert.length + 1);
    el?.focus();
  };

  const onEmoji = (e: PickedEmoji) => {
    if (e.id) {
      const token = `:${e.name}:`;
      tokenMap(channelId)[token] = `<${e.animated ? 'a' : ''}:${e.name}:${e.id}>`;
      insertAtCaret(token);
    } else insertAtCaret(e.name);
  };

  const canSendNow = cooldownLeft === 0;
  const trimmed = text.trim();
  /** Gesendeter Rohtext (`:name:` → `<:name:id>`, Erwähnungen → `<@id>`) – maßgeblich für das Zeichenlimit. */
  const toRawText = (value: string) => {
    const emojis = allServerEmojis(useAppStore.getState());
    return toRaw(value, tokenMap(channelId), (name) => emojis.find((e) => e.name === name && e.serverId === serverId) ?? (perms.allowExternalEmojis ? emojis.find((e) => e.name === name) : undefined));
  };
  const rawLength = toRawText(trimmed).length;
  const over = rawLength > maxLen;
  const hasContent = !!trimmed || files.length > 0;

  const submit = () => {
    if (!hasContent || !canSendNow) return;
    const raw = toRawText(trimmed);
    if (raw.length > maxLen) return;
    const toSend = files;
    setValue('');
    tokenMaps.delete(channelId);
    useMessagesUi.getState().setFiles(channelId, []);
    useMessagesUi.getState().setReply(channelId, null);
    setTrigger(null);
    typingSentAt.delete(channelId);
    void sendMessage({ channelId, content: raw, replyToId: replyTo, files: toSend, slowmodeSeconds: slowmode ? channel.slowmodeSeconds : 0 });
  };

  const sendSticker = (s: StickerDto) => {
    if (!canSendNow) return;
    useMessagesUi.getState().setReply(channelId, null);
    void sendMessage({ channelId, content: '', stickerIds: [s.id], replyToId: replyTo, slowmodeSeconds: slowmode ? channel.slowmodeSeconds : 0 });
  };

  const editLast = (): boolean => {
    const s = useAppStore.getState();
    const items = s.messages[channelId]?.items ?? [];
    for (let i = items.length - 1; i >= 0; i--) {
      const m = items[i]!;
      if (m.author?.id === meId && !m.webhookId && !m.deleted && !isSystemMessage(m)) {
        useMessagesUi.getState().setEditing(channelId, m.id);
        return true;
      }
    }
    return false;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (acOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => (a + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => (a - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        complete(suggestions[active] ?? suggestions[0]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (trigger) setDismissed(`${trigger.start}:${trigger.kind}`);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
      return;
    }
    if (e.key === 'ArrowUp' && !text && !e.shiftKey && !e.altKey) {
      if (editLast()) e.preventDefault();
      return;
    }
    if (e.key === 'Escape' && replyTo) {
      e.preventDefault();
      useMessagesUi.getState().setReply(channelId, null);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setValue(v);
    updateTrigger(v, e.target.selectionStart);
    if (v.trim()) maybeSendTyping(channelId);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const list = e.clipboardData?.files;
    if (list && list.length > 0) {
      e.preventDefault();
      queueFiles(channelId, list, perms.canAttach);
    }
  };

  if (!perms.canSend) {
    return (
      <div className={`composer composer-disabled ${panel ? 'panel' : ''}`} data-testid="composer-disabled" role="note">
        {disabledReason(channel, perms, timeoutUntil)}
      </div>
    );
  }

  const remaining = maxLen - rawLength;
  const listId = `ac-${channelId}`;
  return (
    <form
      className={`composer ${panel ? 'panel' : ''}`}
      data-testid="composer"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {acOpen && (
        <ul className="autocomplete" id={listId} role="listbox" aria-label="Vorschläge" data-testid="autocomplete">
          <li className="autocomplete-title" role="presentation">
            {trigger?.kind === '@' ? 'Mitglieder & Rollen' : trigger?.kind === '#' ? 'Kanäle' : 'Emojis'}
          </li>
          {suggestions.map((s, i) => (
            <li
              key={s.key}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={`autocomplete-item ${i === active ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => complete(s)}
              data-testid="autocomplete-item"
              data-kind={s.kind}
            >
              <SuggestionRow s={s} />
            </li>
          ))}
        </ul>
      )}
      {replyTo && <ReplyBar channelId={channelId} messageId={replyTo} serverId={serverId} />}
      <FileTray channelId={channelId} />
      <div className="composer-box">
        {perms.canAttach && (
          <>
            <Tooltip label="Datei hochladen">
              <button type="button" className="icon-btn composer-attach" onClick={() => fileRef.current?.click()} data-testid="attachment-upload">
                <Plus size={20} />
              </button>
            </Tooltip>
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) queueFiles(channelId, e.target.files, perms.canAttach);
                e.target.value = '';
              }}
              data-testid="attachment-input"
            />
          </>
        )}
        <textarea
          ref={setInput}
          className="composer-input"
          rows={1}
          value={text}
          placeholder={`Nachricht an ${placeholderName}`}
          aria-label={`Nachricht an ${placeholderName}`}
          aria-autocomplete="list"
          aria-controls={acOpen ? listId : undefined}
          aria-expanded={acOpen}
          aria-activedescendant={acOpen ? `${listId}-${active}` : undefined}
          aria-invalid={over}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onSelect={(e) => updateTrigger(e.currentTarget.value, e.currentTarget.selectionStart)}
          onBlur={() => setTrigger(null)}
          data-testid="composer-input"
        />
        <div className="composer-tools">
          <Tooltip label="Sticker">
            <button type="button" className="icon-btn" onClick={(e) => setPicker({ kind: 'sticker', anchor: anchorFrom(e.currentTarget) })} data-testid="composer-sticker">
              <Sticker size={20} />
            </button>
          </Tooltip>
          <Tooltip label="Emoji">
            <button type="button" className="icon-btn" onClick={(e) => setPicker({ kind: 'emoji', anchor: anchorFrom(e.currentTarget) })} data-testid="composer-emoji">
              <Smile size={20} />
            </button>
          </Tooltip>
          <Tooltip label="Senden">
            <button type="submit" className="icon-btn composer-send" disabled={!hasContent || over || !canSendNow} data-testid="composer-send">
              <SendHorizontal size={20} />
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="composer-footer">
        {slowmode && (
          <span className="slowmode" data-testid="slowmode-indicator" aria-live="polite">
            <Timer size={14} aria-hidden="true" />
            {cooldownLeft > 0 ? `Langsamer Modus: noch ${cooldownLeft} s` : `Langsamer Modus: ${channel.slowmodeSeconds} s`}
          </span>
        )}
        {!slowmode && cooldownLeft > 0 && (
          <span className="slowmode" data-testid="slowmode-indicator" aria-live="polite">
            <Timer size={14} aria-hidden="true" /> Bitte warte noch {cooldownLeft} s
          </span>
        )}
        <span className="spacer" />
        {remaining < Math.min(500, maxLen * 0.1) && (
          <span className={`composer-counter ${remaining < 0 ? 'over' : ''}`} data-testid="composer-counter" aria-live="polite">
            {remaining}
          </span>
        )}
      </div>
      {picker?.kind === 'emoji' && (
        <EmojiPickerPopover anchor={picker.anchor} onClose={() => setPicker(null)} serverId={serverId} allowExternal={perms.allowExternalEmojis} placement="top-end" onSelect={onEmoji} />
      )}
      {picker?.kind === 'sticker' && (
        <StickerPickerPopover anchor={picker.anchor} onClose={() => setPicker(null)} serverId={serverId} allowExternal={perms.allowExternalStickers} onSelect={sendSticker} />
      )}
    </form>
  );
}
